import { Component } from "@theme/component";
import { onAnimationEnd } from "@theme/utilities";
import { ThemeEvents } from "@theme/events";

/**
 * Header cart bubble: always reflects Shopify cart totals (`item_count` from `/cart.js`
 * or from `cart:update` payloads). Works with the native cart only; no app is required.
 *
 * Optional: some storefront layers dispatch their own events after mutating cart UI.
 * We listen on `document` and debounce-refetch `/cart.js` so every `<cart-icon>` stays
 * aligned with Shopify. Add event names here if another integration needs the same hook.
 */
const EXTERNAL_CART_BUBBLE_RESYNC_EVENTS = ["rebuy:cart.change"];

/**
 * @param {unknown} cart
 * @returns {number | null}
 */
function parseItemCountFromCartLike(cart) {
  const raw = /** @type {{ item_count?: unknown }} */ (cart)?.item_count;
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw === "string" && raw.trim() !== "") {
    const n = parseInt(raw, 10);
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

/** Coalesce parallel `/cart.js` reads when several `cart-icon` instances handle the same event */
let sharedCartItemCountPromise = /** @type {Promise<number | null> | null} */ (null);

async function fetchShopifyCartItemCount() {
  if (sharedCartItemCountPromise) return sharedCartItemCountPromise;

  sharedCartItemCountPromise = (async () => {
    try {
      const response = await fetch("/cart.js");
      const cart = await response.json();
      return parseItemCountFromCartLike(cart);
    } catch {
      return null;
    } finally {
      sharedCartItemCountPromise = null;
    }
  })();

  return sharedCartItemCountPromise;
}

let cartBubbleShopifyResyncDebounceTimer = /** @type {ReturnType<typeof setTimeout> | null} */ (null);

function scheduleDebouncedSyncAllCartIconsFromShopify() {
  if (cartBubbleShopifyResyncDebounceTimer != null) {
    clearTimeout(cartBubbleShopifyResyncDebounceTimer);
  }
  cartBubbleShopifyResyncDebounceTimer = setTimeout(() => {
    cartBubbleShopifyResyncDebounceTimer = null;
    void (async () => {
      const count = await fetchShopifyCartItemCount();
      if (count == null) return;

      for (const root of document.querySelectorAll("cart-icon")) {
        if (!(root instanceof HTMLElement)) continue;
        const icon = /** @type {any} */ (root);
        if (typeof icon.applyAbsoluteCartCount === "function") {
          icon.applyAbsoluteCartCount(count);
        }
      }
    })();
  }, 50);
}

let cartBubbleExternalSyncListenersBound = false;

function bindCartBubbleExternalSyncListenersOnce() {
  if (typeof document === "undefined" || cartBubbleExternalSyncListenersBound) return;
  cartBubbleExternalSyncListenersBound = true;

  for (const eventName of EXTERNAL_CART_BUBBLE_RESYNC_EVENTS) {
    document.addEventListener(eventName, scheduleDebouncedSyncAllCartIconsFromShopify);
  }
}

bindCartBubbleExternalSyncListenersOnce();

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ["cartBubble", "cartBubbleText", "cartBubbleCount"];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? "0", 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : "";
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener("pageshow", this.onPageShow);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener("pageshow", this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      void this.#syncFromShopify();
    }
  };

  /**
   * @param {CustomEvent} event
   */
  onCartUpdate = async (event) => {
    const resource = event.detail?.resource;
    let count = parseItemCountFromCartLike(resource);

    if (count == null) {
      const fromData = event.detail?.data?.itemCount;
      if (typeof fromData === "number" && Number.isFinite(fromData)) {
        count = fromData;
      }
    }

    if (count == null) {
      count = await fetchShopifyCartItemCount();
    }

    if (count == null) return;

    const animate = count > this.currentCartCount;
    await this.renderCartBubble(count, animate);
  };

  /**
   * Apply a total unit count from Shopify (used by debounced full-cart sync for all icons).
   * @param {number} count
   */
  applyAbsoluteCartCount(count) {
    this.renderCartBubble(count, false);
  }

  #syncFromShopify = async () => {
    const count = await fetchShopifyCartItemCount();
    if (count == null) return;
    this.renderCartBubble(count, false);
  };

  /**
   * Renders the cart bubble with Shopify total units (`item_count`).
   * @param {number} count
   * @param {boolean} [animate=true]
   */
  renderCartBubble = async (count, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle("hidden", count === 0);
    this.refs.cartBubble.classList.toggle("visually-hidden", count === 0);

    this.currentCartCount = count;
    this.#syncAccessibleCount(count);

    this.classList.toggle("header-actions__cart-icon--has-cart", count > 0);

    sessionStorage.setItem(
      "cart-count",
      JSON.stringify({
        value: String(count),
        timestamp: Date.now()
      })
    );

    if (!animate || count === 0) return;

    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add("cart-bubble--animating");
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove("cart-bubble--animating");
  };

  /**
   * @param {number} itemCount
   */
  #syncAccessibleCount(itemCount) {
    const accessibleCount = this.refs.cartBubbleText.querySelector(".visually-hidden");
    if (!accessibleCount) return;

    const label = accessibleCount.textContent?.split(":")[0]?.trim() || "Cart count";
    accessibleCount.textContent = `${label}: ${itemCount}`;
  }
}

if (!customElements.get("cart-icon")) {
  customElements.define("cart-icon", CartIcon);
}
